/**
 * Arabic for the stores and purchasing desk. Proposed; pending native
 * aviation and procurement review.
 *
 * Three terms carry the whole desk and are deliberately kept apart, because
 * the English keeps them apart and the distinction is the point:
 *
 *   on hand   المتوفر فعلياً   — what is physically in the bin
 *   reserved  المحجوز          — promised to an open work order
 *   available المتاح            — what the next job may actually have
 *
 * A translation that rendered any two of these with one word would undo the
 * one property the register exists to have. "On order" (قيد الطلب) is a
 * fourth and is never spoken of as stock.
 *
 * Numbers stay Western (the locale is ar-SA-u-nu-latn), and catalogue numbers,
 * work-order and order numbers — EL-1003, WO-000231, PO-2026-0131 — are left
 * as they are written on the shelf and on the order.
 */
const ar: Record<string, string> = {
  /* ── the desk, its tiles and its board line ────────────────────────── */
  "the stores register": "سجل المخازن",
  "lines in the catalog": "أصناف في الكتالوج",
  "across {n}": "عبر {n}",
  "on the shelf": "على الرف",
  "{n} carry no unit cost and are left out of the total": "{n} بلا تكلفة وحدة، وهي خارج الإجمالي",
  "valued at the last cost recorded for each line": "مقوَّمة بآخر تكلفة مسجلة لكل صنف",
  "out of stock": "نفد من المخزون",
  "{n} of them critical spares": "منها {n} قطع غيار حرجة",
  "none of them a critical spare": "ولا واحدة منها قطعة غيار حرجة",
  "every line has something available": "لكل صنف رصيد متاح",
  "below the reorder point": "دون حد إعادة الطلب",
  "{n} cannot arrive before the shelf empties": "{n} لا يمكن أن تصل قبل نفاد الرف",
  "each can be replenished within its lead time": "كل منها يمكن تجديده خلال مهلة توريده",
  "jobs waiting on a part": "أعمال تنتظر قطعة",
  "{n} with nothing on order": "{n} منها بلا طلب شراء",
  "all of them have a delivery date": "لكلها تاريخ تسليم",
  "no job is short of a part": "لا يوجد عمل ينقصه قطعة",
  "days of cover, median": "أيام التغطية، الوسيط",
  "nothing has been drawn, so no rate can be computed": "لم يُصرف شيء، فلا يمكن حساب معدل",
  "{n} line has no consumption and is left out|{n} lines have no consumption and are left out":
    "لا أصناف بلا استهلاك|صنف واحد بلا استهلاك وهو مستثنى|صنفان بلا استهلاك وهما مستثنيان|{n} أصناف بلا استهلاك وهي مستثناة|{n} صنفاً بلا استهلاك وهي مستثناة|{n} صنف بلا استهلاك وهو مستثنى",
  "issues filled from stock": "الصرف المُلبّى من المخزون",
  "nothing has been asked of the stores in the window": "لم يُطلب من المخازن شيء خلال الفترة",
  "{filled} of {asked} reservations filled in full": "{filled} من {asked} حجوزات لُبّيت بالكامل",
  "stock count accuracy": "دقة الجرد",
  "nothing has been counted in the last year": "لم يُجرد شيء خلال العام الماضي",
  "{counted} lines counted, {uncounted} never counted": "{counted} أصناف مجرودة، و{uncounted} لم تُجرد قط",
  "{site} holds {lines} in stores, {value} on the shelf.": "{site} يحتفظ بـ{lines} في المخازن، بقيمة {value} على الرف.",
  "nothing valued": "لا شيء مقوَّم",
  "{out} out of stock, {below} below the reorder point, {expired} past their shelf life.":
    "{out} نفدت، و{below} دون حد إعادة الطلب، و{expired} انتهت صلاحيتها.",
  "{n} waiting on a part, and {blocked} of those have nothing on order.":
    "{n} تنتظر قطعة، و{blocked} منها بلا طلب شراء.",
  "{n} waiting on a part, all with a delivery date.": "{n} تنتظر قطعة، ولكلها تاريخ تسليم.",
  "Critical spares not held: {list}.": "قطع غيار حرجة غير متوفرة: {list}.",
  "{n} promised before today and not delivered.": "{n} وُعد بها قبل اليوم ولم تُسلَّم.",

  /* ── what the desk cannot tell you ─────────────────────────────────── */
  "this register is entered, not read from a stores system: the platform declares no stock item, so none of these lines is watched by the rules engine and no reorder raises an alert on its own":
    "هذا السجل مُدخل يدوياً ولا يُقرأ من نظام مخازن: المنصة لا تُعرِّف صنف مخزون، فلا صنف من هذه يراقبه محرك القواعد، ولا تُطلق إعادة طلب تنبيهاً من تلقاء نفسها",
  "consumption is counted from the movements recorded here; anything drawn from the shelf without being booked out is invisible to every figure on this desk":
    "يُحسب الاستهلاك من الحركات المسجلة هنا؛ وأي شيء يُؤخذ من الرف دون قيد صرف لا يظهر في أي رقم على هذه الشاشة",
  "unit costs are the last price recorded against a line, not a valuation, and no line is revalued over time":
    "تكاليف الوحدة هي آخر سعر مسجل للصنف، لا تقييماً، ولا يُعاد تقييم أي صنف بمرور الوقت",
  "{n} have never been counted, so the accuracy above is measured over the rest":
    "{n} لم تُجرد قط، فالدقة أعلاه محسوبة على الباقي",
  "{n} have never been drawn, so they have no consumption rate and their cover is unknown rather than long":
    "{n} لم يُصرف منها شيء قط، فلا معدل استهلاك لها وتغطيتها غير معروفة لا طويلة",

  /* ── the jobs, the run, the dates ──────────────────────────────────── */
  "nothing on order": "بلا طلب شراء",
  "due today": "مستحق اليوم",
  "no date given": "لا تاريخ محدد",
  "no date": "لا تاريخ",
  "cannot say": "لا يمكن الجزم",
  "To buy": "للشراء",
  "Cover left": "التغطية المتبقية",
  "Arrives in time?": "هل يصل في الوقت؟",
  "total unknown — a line carries no cost": "الإجمالي غير معروف — أحد الأصناف بلا تكلفة",
  "No open job is short of a part it has reserved.": "لا يوجد عمل مفتوح تنقصه قطعة حجزها.",
  "nothing on order: this job is stopped, not waiting": "بلا طلب شراء: هذا العمل متوقف، لا منتظر",

  /* ── Sky's own answers ─────────────────────────────────────────────── */
  "Every line in the catalog has something available.": "لكل صنف في الكتالوج رصيد متاح.",
  "No line is marked as a critical spare.": "لا صنف مُعلَّم كقطعة غيار حرجة.",
  "Nothing is below its reorder point.": "لا شيء دون حد إعادة طلبه.",
  " — {n} days too late at this lead time": " — متأخر {n} يوماً بمهلة التوريد هذه",
  "No job is short of a part.": "لا يوجد عمل تنقصه قطعة.",
  "No purchase order is past the date the supplier promised.": "لا يوجد أمر شراء تجاوز التاريخ الذي وعد به المورّد.",
  "{number} from {supplier} — {days} late, {lines} outstanding":
    "{number} من {supplier} — متأخر {days}، و{lines} لم تُسلَّم",
  "Every line with stock has moved within the year.": "كل صنف له رصيد تحرك خلال العام.",
  "No line carries a unit cost, so the shelf cannot be valued.": "لا صنف يحمل تكلفة وحدة، فلا يمكن تقويم الرف.",
  "{value} on the shelf, at the last cost recorded for each line. {unknown} lines carry no cost.":
    "{value} على الرف، بآخر تكلفة مسجلة لكل صنف. و{unknown} أصناف بلا تكلفة.",
  "These lines have no supplier recorded: {list}": "هذه الأصناف بلا مورّد مسجل: {list}",
  "Every line has a supplier recorded against it.": "لكل صنف مورّد مسجل.",

  /* ── counts ────────────────────────────────────────────────────────── */
  "{n} category|{n} categories": "لا فئات|فئة واحدة|فئتان|{n} فئات|{n} فئة|{n} فئة",
  "{n} line|{n} lines": "لا أصناف|صنف واحد|صنفان|{n} أصناف|{n} صنفاً|{n} صنف",
  "{n} job is|{n} jobs are": "لا أعمال|عمل واحد|عملان|{n} أعمال|{n} عملاً|{n} عمل",
  "{n} purchase order was|{n} purchase orders were":
    "لا أوامر شراء|أمر شراء واحد|أمرا شراء|{n} أوامر شراء|{n} أمر شراء|{n} أمر شراء",
  "{n} part short|{n} parts short": "لا نقص|تنقصه قطعة|تنقصه قطعتان|تنقصه {n} قطع|تنقصه {n} قطعة|تنقصه {n} قطعة",
  "the delivery is {n} day late|the delivery is {n} days late":
    "التسليم في موعده|التسليم متأخر يوماً|التسليم متأخر يومين|التسليم متأخر {n} أيام|التسليم متأخر {n} يوماً|التسليم متأخر {n} يوم",
  "due in {n} day|due in {n} days":
    "مستحق اليوم|مستحق خلال يوم|مستحق خلال يومين|مستحق خلال {n} أيام|مستحق خلال {n} يوماً|مستحق خلال {n} يوم",
  "{n} day late|{n} days late": "في الموعد|متأخر يوماً|متأخر يومين|متأخر {n} أيام|متأخر {n} يوماً|متأخر {n} يوم",
  "{n} day too late|{n} days too late":
    "في الوقت|متأخر يوماً|متأخر يومين|متأخر {n} أيام|متأخر {n} يوماً|متأخر {n} يوم",
  "{n} purchase order was promised before today and has not arrived.|{n} purchase orders were promised before today and have not arrived.":
    "لا أوامر شراء متأخرة.|أمر شراء واحد وُعد به قبل اليوم ولم يصل.|أمرا شراء وُعد بهما قبل اليوم ولم يصلا.|{n} أوامر شراء وُعد بها قبل اليوم ولم تصل.|{n} أمر شراء وُعد بها قبل اليوم ولم تصل.|{n} أمر شراء وُعد به قبل اليوم ولم يصل.",
  "{n} line cannot arrive before its shelf empties, at the lead time recorded for it. The reorder point itself is too low, not merely the stock.|{n} lines cannot arrive before their shelves empty, at the lead times recorded for them. Their reorder points are too low, not merely their stock.":
    "لا صنف كهذا.|صنف واحد لا يمكن أن يصل قبل نفاد رفه بمهلة التوريد المسجلة له. حد إعادة الطلب نفسه منخفض، وليست المشكلة في الرصيد وحده.|صنفان لا يمكن أن يصلا قبل نفاد رفيهما بمهلتي التوريد المسجلتين لهما. حدّا إعادة الطلب نفساهما منخفضان، وليست المشكلة في الرصيد وحده.|{n} أصناف لا يمكن أن تصل قبل نفاد رفوفها بمهل التوريد المسجلة لها. حدود إعادة الطلب نفسها منخفضة، وليست المشكلة في الرصيد وحده.|{n} صنفاً لا يمكن أن تصل قبل نفاد رفوفها بمهل التوريد المسجلة لها. حدود إعادة الطلب نفسها منخفضة، وليست المشكلة في الرصيد وحده.|{n} صنف لا يمكن أن يصل قبل نفاد رفه بمهلة التوريد المسجلة له. حد إعادة الطلب نفسه منخفض، وليست المشكلة في الرصيد وحده.",

  /* ── what the stores owe a job ─────────────────────────────────────── */
  "No parts are reserved against this job.": "لا قطع محجوزة على هذا العمل.",
  "Every part is issued.": "صُرفت كل القطع.",
  "The last part is due today.": "آخر قطعة مستحقة اليوم.",
  "{n} part is on the shelf, reserved and not yet collected.|{n} parts are on the shelf, reserved and not yet collected.":
    "لا قطع على الرف.|قطعة واحدة على الرف، محجوزة ولم تُستلم بعد.|قطعتان على الرف، محجوزتان ولم تُستلما بعد.|{n} قطع على الرف، محجوزة ولم تُستلم بعد.|{n} قطعة على الرف، محجوزة ولم تُستلم بعد.|{n} قطعة على الرف، محجوزة ولم تُستلم بعد.",
  "{n} part is short and nothing is on order for it.|{n} parts are short and nothing is on order for them.":
    "لا نقص.|تنقص قطعة واحدة ولا طلب شراء لها.|تنقص قطعتان ولا طلب شراء لهما.|تنقص {n} قطع ولا طلب شراء لها.|تنقص {n} قطعة ولا طلب شراء لها.|تنقص {n} قطعة ولا طلب شراء لها.",
  "{n} part is short.|{n} parts are short.":
    "لا نقص.|تنقص قطعة واحدة.|تنقص قطعتان.|تنقص {n} قطع.|تنقص {n} قطعة.|تنقص {n} قطعة.",
  "The last part was due {n} day ago and has not arrived.|The last part was due {n} days ago and has not arrived.":
    "آخر قطعة مستحقة اليوم ولم تصل.|كانت آخر قطعة مستحقة قبل يوم ولم تصل.|كانت آخر قطعة مستحقة قبل يومين ولم تصل.|كانت آخر قطعة مستحقة قبل {n} أيام ولم تصل.|كانت آخر قطعة مستحقة قبل {n} يوماً ولم تصل.|كانت آخر قطعة مستحقة قبل {n} يوم ولم تصل.",
  "The job can start in {n} day, when the last part is due.|The job can start in {n} days, when the last part is due.":
    "يمكن أن يبدأ العمل اليوم.|يمكن أن يبدأ العمل خلال يوم، حين تُستحق آخر قطعة.|يمكن أن يبدأ العمل خلال يومين، حين تُستحق آخر قطعة.|يمكن أن يبدأ العمل خلال {n} أيام، حين تُستحق آخر قطعة.|يمكن أن يبدأ العمل خلال {n} يوماً، حين تُستحق آخر قطعة.|يمكن أن يبدأ العمل خلال {n} يوم، حين تُستحق آخر قطعة.",

  /* ── the refusals ──────────────────────────────────────────────────── */
  "An issue must be of at least one unit.": "الصرف لا يقل عن وحدة واحدة.",
  "{sku} is not in the catalog.": "{sku} ليس في الكتالوج.",
  "{location} holds {here} {uom}. Issuing {qty} would take the bin below nothing, which the shelf cannot do.":
    "{location} به {here} {uom}. صرف {qty} ينزل بالرصيد تحت الصفر، وهو ما لا يفعله الرف.",
  "{n} issued against the reservation for {wo}.": "صُرفت {n} على حساب الحجز الخاص بـ{wo}.",
  "A return must be of at least one unit.": "الإرجاع لا يقل عن وحدة واحدة.",
  "An adjustment of nothing is not a record of anything.": "تسوية بلا كمية ليست سجلاً لشيء.",
  "An adjustment must say why. A figure changed without a reason cannot be investigated later.":
    "التسوية يجب أن تذكر السبب. رقم يتغير بلا سبب لا يمكن التحقيق فيه لاحقاً.",
  "{location} holds {here} {uom}; it cannot be reduced by {qty}.": "{location} به {here} {uom}؛ ولا يمكن خصم {qty} منه.",
  "A count cannot be negative.": "الجرد لا يكون بالسالب.",
  "The count agreed with the register.": "الجرد طابق السجل.",
  "The count differed by {n}; the register has been brought to the counted figure and the difference recorded.":
    "اختلف الجرد بمقدار {n}؛ وسُوِّي السجل على الرصيد المجرود وسُجِّل الفرق.",
  "A transfer must be between two different stores.": "النقل يكون بين مخزنين مختلفين.",
  "A transfer must be of at least one unit.": "النقل لا يقل عن وحدة واحدة.",
  "{location} holds {here} {uom}, which is less than {qty}.": "{location} به {here} {uom}، وهو أقل من {qty}.",
  "A reservation must be of at least one unit.": "الحجز لا يقل عن وحدة واحدة.",
  "A reservation must name the work order it is for.": "الحجز يجب أن يذكر أمر العمل الذي هو له.",
  "Recorded. {n} of these are not on the shelf, so the line now shows a shortage against {wo}.":
    "سُجِّل. {n} منها ليست على الرف، فصار الصنف يُظهر نقصاً على {wo}.",
  "Releasing a reservation must say why, so the job's record shows what changed.":
    "فك الحجز يجب أن يذكر السبب، ليُظهر سجل العمل ما الذي تغيّر.",
  "That reservation is no longer on the register.": "ذلك الحجز لم يعد في السجل.",
  "That reservation has already been released.": "ذلك الحجز فُك من قبل.",
  "A requisition needs at least one line with a quantity.": "طلب الشراء يحتاج صنفاً واحداً على الأقل بكمية.",
  "Every line must say why it is being bought. A purchase nobody can account for is one nobody approves twice.":
    "كل صنف يجب أن يذكر سبب شرائه. شراء لا يستطيع أحد تبريره لا يُعتمد مرتين.",
  "Raised as {number}.": "رُفع برقم {number}.",
  "{number} is not on the register.": "{number} ليس في السجل.",
  "{number} is {status} and is not waiting on a decision.": "{number} حالته {status} وليس بانتظار قرار.",
  "{who} raised {number}. Nobody approves their own requisition.":
    "{who} هو من رفع {number}. لا أحد يعتمد طلبه بنفسه.",
  "{number} has a line with no price, so its value is unknown. An approval of an unknown amount is not an approval.":
    "{number} به صنف بلا سعر، فقيمته غير معروفة. واعتماد مبلغ غير معروف ليس اعتماداً.",
  "{value} {currency} is above the {role}'s limit of {limit}. It goes to the next authority.":
    "{value} {currency} تتجاوز حد {role} البالغ {limit}. ويُرفع إلى الصلاحية الأعلى.",
  "{number} is {status}. Only an approved requisition becomes an order.":
    "{number} حالته {status}. ولا يصير أمر شراء إلا طلب معتمد.",
  "An order must name the supplier it goes to.": "أمر الشراء يجب أن يسمي المورّد الموجَّه إليه.",
  "Ordered as {number}.": "صدر أمر الشراء برقم {number}.",
  "{number} was canceled.": "{number} أُلغي.",
  "A receipt must record something received or something rejected.":
    "قيد الاستلام يجب أن يسجل شيئاً مستلماً أو شيئاً مرفوضاً.",
  "{sku} is not on {number}.": "{sku} ليس ضمن {number}.",
  "{number} has {owed} of {sku} outstanding; {qty} cannot be received against it. Record the surplus as a separate order or send it back.":
    "{number} به {owed} من {sku} لم تُسلَّم؛ ولا يمكن استلام {qty} عليه. سجّل الزائد كأمر شراء منفصل أو أعده.",
  "Received. {number} still has {n} outstanding.": "استُلم. ولا يزال في {number} {n} لم تُسلَّم.",
  "{number} is complete.": "{number} اكتمل.",
  "Canceling an order must say why.": "إلغاء أمر الشراء يجب أن يذكر السبب.",
  "{number} has already been delivered against and cannot be canceled. Close the outstanding lines instead.":
    "{number} سُلِّم عليه بالفعل ولا يمكن إلغاؤه. أغلق الأصناف غير المسلَّمة بدلاً من ذلك.",
  "Order canceled: {reason}": "أُلغي أمر الشراء: {reason}",
  "A line needs a catalog number.": "الصنف يحتاج رقم كتالوج.",
  "A line needs a name.": "الصنف يحتاج اسماً.",
  "{sku} is already in the catalog.": "{sku} موجود في الكتالوج بالفعل.",
  "A reorder point and a maximum cannot be negative.": "حد إعادة الطلب والحد الأقصى لا يكونان بالسالب.",
  "The maximum cannot be below the reorder point.": "الحد الأقصى لا يقل عن حد إعادة الطلب.",
  "Added to the catalog.": "أُضيف إلى الكتالوج.",
  "Replenishment: {state}.": "تجديد مخزون: {state}.",
  "Raised by the replenishment run for {supplier}.": "رُفع ضمن دورة التجديد الخاصة بـ{supplier}.",
  "Raised {n} for approval: {list}.": "رُفع {n} للاعتماد: {list}.",
  "one requisition": "طلب شراء واحد",
  "{n} requisitions": "{n} طلبات شراء",
  "Recorded.": "سُجِّل.",

  /* ── purchasing ────────────────────────────────────────────────────── */
  "Deciding as": "يقرر بصفة",
  "{role} — no limit": "{role} — بلا حد",
  "{role} — up to {limit}": "{role} — حتى {limit}",
  "{role}, no limit": "{role}، بلا حد",
  "{role}, up to {limit}": "{role}، حتى {limit}",
  "These limits are proposed, not an airport's policy: the platform issues access levels, which say what somebody may open, not what they may commit.":
    "هذه الحدود مقترحة وليست سياسة مطار: المنصة تصدر مستويات وصول تحدد ما يجوز فتحه، لا ما يجوز الالتزام به مالياً.",
  "Waiting on a decision": "بانتظار قرار",
  "No requisition is waiting on a decision.": "لا طلب شراء بانتظار قرار.",
  Requisition: "طلب الشراء",
  "Raised by": "رفعه",
  "Approved, waiting to be ordered": "معتمد وبانتظار أمر الشراء",
  "Approved by": "اعتمده",
  "Raise the order": "إصدار أمر الشراء",
  "On order": "قيد الطلب",
  "Nothing is on order.": "لا شيء قيد الطلب.",
  Supplier: "المورّد",
  Outstanding: "لم تُسلَّم",
  Receive: "الاستلام",
  "from {n}": "من {n}",
  "under {ref}": "بموجب {ref}",
  "Book in": "قيد استلام",
  "Raise the order for {number}": "إصدار أمر الشراء لـ{number}",
  "The date offered is today plus the longest lead time recorded on the lines, {n}. It is a supplier's promise once they give one, not a forecast.":
    "التاريخ المقترح هو اليوم زائد أطول مهلة توريد مسجلة على الأصناف، {n}. ويصير وعد مورّد متى أعطاه، لا تنبؤاً.",
  "No lead time is recorded on these lines, so no date is offered. Enter what the supplier promises.":
    "لا مهلة توريد مسجلة على هذه الأصناف، فلا تاريخ مقترح. أدخل ما يعد به المورّد.",
  "Book in {number}": "قيد استلام {number}",
  "From {supplier}": "من {supplier}",
  "Delivery note": "إشعار التسليم",
  "Into which store": "إلى أي مخزن",
  "Accepted, {sku}": "المقبول، {sku}",
  "Rejected, {sku}": "المرفوض، {sku}",
  "Why anything was rejected": "سبب رفض ما رُفض",
  "What is rejected stays off the shelf and outstanding on the order. The airport is still waiting for it.":
    "ما يُرفض يبقى خارج الرف وغير مسلَّم على أمر الشراء. فالمطار لا يزال ينتظره.",
  "Book it in": "قيده",

  /* ── the item page ─────────────────────────────────────────────────── */
  "{site} · Stores": "{site} · المخازن",
  "{site} · Store & Warehouse": "{site} · المخازن والمستودع",
  "{sku} is not a line in this catalog.": "{sku} ليس صنفاً في هذا الكتالوج.",
  "Back to the stores": "العودة إلى المخازن",
  Return: "الإرجاع",
  "Reserve for a job": "حجز لعمل",
  Count: "الجرد",
  Adjust: "تسوية",
  "A critical spare: {why}": "قطعة غيار حرجة: {why}",
  "On hand": "المتوفر فعلياً",
  "Promised to open jobs": "المحجوز لأعمال مفتوحة",
  "Available to the next job": "المتاح للعمل التالي",
  "Reorder point / maximum": "حد إعادة الطلب / الحد الأقصى",
  "Used in the last year": "المستهلك خلال العام",
  "Days of cover": "أيام التغطية",
  "unknown — nothing has been drawn, so there is no rate": "غير معروف — لم يُصرف شيء، فلا معدل",
  "Lead time": "مهلة التوريد",
  "Last counted": "آخر جرد",
  "{at} by {by}, {variance}": "{at} بواسطة {by}، {variance}",
  "agreed with the register": "طابق السجل",
  "differed by {n}": "اختلف بمقدار {n}",
  "never counted": "لم يُجرد قط",
  "Shelf life": "مدة الصلاحية",
  "nothing named": "لا شيء محدد",
  "No open job has this line reserved.": "لا عمل مفتوح حاجز لهذا الصنف.",
  "Work order": "أمر العمل",
  "On the shelf": "على الرف",
  issued: "صُرفت",
  "on the shelf, waiting to be collected": "على الرف، بانتظار الاستلام",
  "Parts from the stores": "قطع من المخازن",
  "what is reserved against this job, and whether it is here": "ما هو محجوز على هذا العمل، وهل هو حاضر",
  Wanted: "المطلوب",
  Needed: "تاريخ الحاجة",
  "Reserved by": "حجزه",
  "released from the stores desk": "فُك من شاشة المخازن",
  Release: "فك الحجز",
  "Nothing has moved yet.": "لم تحدث حركة بعد.",
  What: "الحركة",
  Quantity: "الكمية",
  Store: "المخزن",
  "Counted quantity": "الكمية المجرودة",
  "Change by (negative to reduce)": "التغيير بمقدار (سالب للخصم)",
  "Needed by": "مطلوب بحلول",
  "Why (required)": "السبب (إلزامي)",
  "The register says {n} {uom} in this store. A count that agrees is recorded too — it is the only evidence the accuracy figure has.":
    "السجل يقول {n} {uom} في هذا المخزن. والجرد المطابق يُسجَّل أيضاً — فهو الدليل الوحيد الذي تقوم عليه نسبة الدقة.",

  /* ── the desk's own page furniture ─────────────────────────────────── */
  "Raise the replenishment": "رفع طلبات التجديد",
  "What the airport holds on the shelf, what is promised to open jobs, what is on order and what is about to run out. On hand, reserved and available are three different numbers and are never shown as one; stock on order is shown beside them and never added to them.":
    "ما يحتفظ به المطار على الرف، وما هو محجوز لأعمال مفتوحة، وما هو قيد الطلب، وما يوشك على النفاد. المتوفر فعلياً والمحجوز والمتاح ثلاثة أرقام مختلفة ولا تُعرض كرقم واحد أبداً؛ وما هو قيد الطلب يُعرض بجوارها ولا يُضاف إليها.",
  "Entered, not read from a stores system: the platform declares no stock item, so none of these lines is watched by the rules engine and nothing here reorders itself. Consumption is counted from the movements recorded here — anything taken off the shelf without being booked out is invisible to every figure below.":
    "مُدخل يدوياً ولا يُقرأ من نظام مخازن: المنصة لا تُعرِّف صنف مخزون، فلا صنف من هذه يراقبه محرك القواعد ولا شيء هنا يُعيد طلب نفسه. ويُحسب الاستهلاك من الحركات المسجلة هنا — وأي شيء يُؤخذ من الرف دون قيد صرف لا يظهر في أي رقم أدناه.",
  "Critical spares": "قطع الغيار الحرجة",
  "Promised to a job": "محجوز لعمل",
  "Not moved in a year": "بلا حركة منذ عام",
  "Every category": "كل الفئات",
  "Every store": "كل المخازن",
  "No line matches that.": "لا صنف يطابق ذلك.",
  Reserved: "المحجوز",
  Available: "المتاح",
  Cover: "التغطية",
  "Ask about the stores": "اسأل عن المخازن",
  "answered from the register; a model may explain but may not add a figure":
    "يُجاب من السجل؛ وللنموذج أن يشرح لا أن يضيف رقماً",
  "What is stopping a job?": "ما الذي يوقف عملاً؟",
  CRITICAL: "حرجة",
  "nothing drawn in a year": "لم يُصرف منها شيء منذ عام",
  "nothing has been drawn, so there is no rate to divide by": "لم يُصرف شيء، فلا معدل يُقسم عليه",
  "no cost": "بلا تكلفة",

  /* ── the catalogue, the states and the movements ───────────────────
   *
   * Rendered through `t(variable)` from the domain's own tables, which the
   * extractor cannot see. Pinned by src/domain/__tests__/stores.test.ts.
   */
  "Stores and purchasing": "المخازن والمشتريات",
  "General consumables": "مستهلكات عامة",
  "Electrical spares": "قطع غيار كهربائية",
  "Civil maintenance": "صيانة مدنية",
  "Equipment and plant": "المعدات والآليات",
  Tools: "العدد والأدوات",
  "BHS system parts": "قطع نظام مناولة الحقائب",
  "Utilities parts": "قطع المرافق",
  "Paints and markings": "الدهانات والعلامات",
  "Fencing and perimeter": "الأسوار والمحيط",
  "Electro-mechanical parts": "قطع كهروميكانيكية",
  "Out of stock": "نفد من المخزون",
  "Past its shelf life": "انتهت صلاحيته",
  "Below reorder point": "دون حد إعادة الطلب",
  "At reorder point": "عند حد إعادة الطلب",
  "Above maximum": "فوق الحد الأقصى",
  "In stock": "متوفر",
  Received: "استُلم",
  Returned: "أُرجع",
  Adjusted: "سُوِّي",
  Counted: "جُرد",
  "No supplier recorded": "لا مورّد مسجل",
  "Lines in stock": "الأصناف المتوفرة",
  "Lines out of stock": "الأصناف التي نفدت",
  "Lines below reorder point": "الأصناف دون حد إعادة الطلب",
  "Days of cover, median": "أيام التغطية، الوسيط",
  "Critical spares held": "قطع الغيار الحرجة المتوفرة",
  "Issues filled from stock": "الصرف المُلبّى من المخزون",
  "Stock count accuracy": "دقة الجرد",

  /* ── the cards' titles and subtitles ───────────────────────────────── */
  "Jobs waiting on a part": "أعمال تنتظر قطعة",
  "read from the job's side: what the stores owe, and whether it is coming":
    "مقروءة من جهة العمل: ما الذي تدين به المخازن، وهل هو في الطريق",
  "What must be bought": "ما يجب شراؤه",
  "grouped by supplier, because that is what becomes one order, one delivery and one invoice":
    "مجمّعة حسب المورّد، لأن ذلك ما يصير أمر شراء واحداً وتسليماً واحداً وفاتورة واحدة",
  Purchasing: "المشتريات",
  "what has been asked for, what was approved and by whom, and what the suppliers owe":
    "ما طُلب، وما اعتُمد ومن اعتمده، وما يدين به المورّدون",
  "The catalogue": "الكتالوج",
  "worst state first, then the soonest to run out": "الأسوأ حالاً أولاً، ثم الأقرب إلى النفاد",
  "What this desk cannot tell you": "ما لا تستطيع هذه الشاشة إخبارك به",
  "stated rather than left to be discovered": "مذكور بدل أن يُترك ليُكتشف",
  "on hand, promised and available are three different numbers":
    "المتوفر فعلياً والمحجوز والمتاح ثلاثة أرقام مختلفة",
  "In which store": "في أي مخزن",
  "the shelf, bin by bin": "الرف، صندوقاً صندوقاً",
  "Who is waiting for it": "من ينتظره",
  "open reservations against work orders": "الحجوزات المفتوحة على أوامر العمل",
  "What is coming": "ما هو في الطريق",
  "a supplier's promise, which is not stock": "وعد مورّد، وهو ليس مخزوناً",
  "Everything that has happened to it": "كل ما حدث له",
  "append-only: a correction is another movement, never an edit":
    "الإضافة فقط: التصحيح حركة أخرى، لا تعديلاً",
  "Issue to a job": "صرف لعمل",
  "Return to the shelf": "إرجاع إلى الرف",
  "Adjust the figure": "تسوية الرصيد",
  "Count what is on the shelf": "جرد ما على الرف",
  "Move between stores": "نقل بين المخازن",
  "Not in the catalogue": "ليس في الكتالوج",

  /* ── getting a real store into the register (2026-09-21) ──────────────
   *
   * On the terms: رصيد افتتاحي (opening balance) is what the store declares
   * it holds; جرد (a count) is what somebody found on walking the shelves.
   * The Arabic keeps them as far apart as the English does, because the
   * count-accuracy figure is computed from the second alone.
   */
  "Load the stock": "تحميل المخزون",
  "Add a line": "إضافة صنف",
  "Add a line to the catalog": "إضافة صنف إلى الكتالوج",
  "Book in a delivery": "قيد استلام توريد",
  "Book in a delivery with no order": "قيد استلام توريد بلا أمر شراء",
  "Record an order": "تسجيل أمر شراء",
  "Record an order raised elsewhere": "تسجيل أمر شراء صادر من نظام آخر",
  "Take the spreadsheet the store is already run from. Save it as CSV and choose it here, or copy the rows and paste them below. Nothing is saved until you have seen what it would do.":
    "خذ الجدول الذي يُدار به المخزن أصلاً. احفظه بصيغة CSV واخترْه هنا، أو انسخ الصفوف والصقها أدناه. ولا يُحفظ شيء قبل أن ترى ما الذي سيحدث.",
  "Is this the opening stock, or a stock count?": "هل هذا رصيد افتتاحي أم جرد؟",
  "The opening stock — this is what we hold": "رصيد افتتاحي — هذا ما لدينا",
  "A stock count — we walked the shelves": "جرد — مشينا على الرفوف",
  "An opening balance says what is on the shelves. It is not evidence about anything, so it does not touch the count-accuracy figure.":
    "الرصيد الافتتاحي يقول ما على الرفوف. وهو ليس دليلاً على شيء، فلا يمس نسبة دقة الجرد.",
  "A stock count is evidence: the differences it finds are real discrepancies, and the count-accuracy figure on the desk is computed from these and nothing else.":
    "الجرد دليل: الفروق التي يجدها فروق حقيقية، ونسبة دقة الجرد في هذه الشاشة محسوبة منها وحدها.",
  "Choose a CSV file": "اختر ملف CSV",
  "Or paste the rows, with their heading row": "أو الصق الصفوف مع صف العناوين",
  "Which column headings are understood": "أي عناوين الأعمدة مفهومة",
  "Only the catalog number is required, and a name for anything the catalog has not seen before.":
    "المطلوب فقط رقم الكتالوج، واسمٌ لكل صنف لم يره الكتالوج من قبل.",
  "Show me what it would do": "أرني ما الذي سيحدث",
  "Load it in": "حمّله",
  "Record the count": "تسجيل الجرد",
  "What would happen": "ما الذي سيحدث",
  "Quantity in {store}": "الكمية في {store}",
  "also held elsewhere and untouched by this: {list}":
    "موجود أيضاً في مواضع أخرى لا يمسها هذا: {list}",
  "These columns were not recognized and nothing was taken from them: {list}":
    "لم تُعرف هذه الأعمدة ولم يُؤخذ منها شيء: {list}",
  "{n} more lines are not shown here; all of them will be applied.":
    "{n} أصناف أخرى غير معروضة هنا؛ وستُطبَّق كلها.",
  "new to the catalog": "جديد على الكتالوج",
  "already as recorded": "مطابق لما هو مسجل",
  "{n} new to the catalog": "{n} جديد على الكتالوج",
  "{n} changed": "{n} تغيّر",
  "{n} already as recorded": "{n} مطابق لما هو مسجل",
  "{n} that cannot be read and will be left out": "{n} تعذّرت قراءتها وستُستثنى",
  "Nothing in the file to apply.": "لا شيء في الملف لتطبيقه.",
  "There is nothing in the file to apply.": "لا يوجد في الملف ما يُطبَّق.",
  "Every line in the file has something wrong with it. Nothing was applied.":
    "كل صنف في الملف به خلل. لم يُطبَّق شيء.",
  "The file needs a heading row and at least one line beneath it.":
    "الملف يحتاج صف عناوين وصنفاً واحداً على الأقل تحته.",
  "No catalog number column. Every line needs one — a heading called SKU, code, item number or part number.":
    "لا يوجد عمود لرقم الكتالوج. كل صنف يحتاج واحداً — عنوان مثل SKU أو رمز أو رقم الصنف أو رقم القطعة.",

  /* what a cell could not say */
  "no catalog number": "بلا رقم كتالوج",
  "this row has {has} cells and the heading row has {wants} — one missing comma shifts every value after it into the wrong column":
    "هذا الصف به {has} خانات وصف العناوين به {wants} — فاصلة واحدة ناقصة تزيح كل قيمة بعدها إلى العمود الخطأ",
  "no number in it": "لا رقم فيه",
  "not a number": "ليس رقماً",
  "a quantity cannot be negative": "الكمية لا تكون بالسالب",
  "not a category this register holds": "ليست فئة يحتفظ بها هذا السجل",
  "not a date this can read — use YYYY-MM-DD": "ليس تاريخاً يمكن قراءته — استخدم YYYY-MM-DD",
  "a line the catalog has never seen needs a name": "الصنف الذي لم يره الكتالوج من قبل يحتاج اسماً",
  "no category given — it will be filed under general consumables":
    "لم تُذكر فئة — سيُصنَّف ضمن المستهلكات العامة",
  "the same catalog number is already on line {n} of this file":
    "رقم الكتالوج نفسه موجود في السطر {n} من هذا الملف",
  "rows pasted on {date}": "صفوف مُلصقة بتاريخ {date}",

  /* what the movements say afterwards */
  "Opening balance from {source}.": "رصيد افتتاحي من {source}.",
  "Counted on to the register from {source}.": "أُدخل بالجرد إلى السجل من {source}.",
  "Stock count loaded from {source}.": "جرد مخزون مُحمَّل من {source}.",
  "marked critical in the file this was loaded from": "مُعلَّم كحرج في الملف الذي حُمِّل منه",
  "marked critical in {source}": "مُعلَّم كحرج في {source}",
  "Line details corrected: {fields}.": "صُحِّحت بيانات الصنف: {fields}.",
  "{n} added to the catalog": "{n} أُضيف إلى الكتالوج",
  "{n} corrected": "{n} صُحِّح",
  "{n} counted": "{n} جُرد",
  "{n} given an opening balance": "{n} أُعطي رصيداً افتتاحياً",
  "{n} left out because they could not be read": "{n} استُثني لتعذّر قراءته",

  /* the typed line */
  "Catalog number": "رقم الكتالوج",
  "Quantity on the shelf now": "الكمية على الرف الآن",
  "Reorder point": "حد إعادة الطلب",
  Maximum: "الحد الأقصى",
  "Unit cost": "تكلفة الوحدة",
  "Lead time (days)": "مهلة التوريد (أيام)",
  Unit: "الوحدة",
  Bin: "الصندوق",
  "If it is a critical spare, why": "إن كانت قطعة غيار حرجة، فلماذا",
  "what stops if it is not held": "ما الذي يتوقف إن لم تتوفر",
  "A criticality with no reason against it is the first thing a cost review deletes, so this asks for one.":
    "الأهمية الحرجة بلا سبب مسجل هي أول ما تحذفه مراجعة التكاليف، ولهذا يُطلب السبب.",

  /* a delivery with no order */
  "A direct purchase, an emergency buy, free issue from a contractor, a transfer in from another airport. The stock goes on the shelf and the record says there was no order behind it.":
    "شراء مباشر، أو شراء طارئ، أو توريد مجاني من مقاول، أو نقل من مطار آخر. تدخل الكمية إلى الرف ويذكر السجل أنه لم يكن خلفها أمر شراء.",
  "Why it came in without an order (required)": "سبب دخوله بلا أمر شراء (إلزامي)",
  "Unit cost, if it is known": "تكلفة الوحدة، إن كانت معروفة",
  "A delivery must record at least one line with a quantity.": "قيد الاستلام يجب أن يسجل صنفاً واحداً على الأقل بكمية.",
  "A delivery with no purchase order behind it must say why it came in that way — a direct purchase, an emergency, free issue from a contractor.":
    "التوريد الذي لا أمر شراء خلفه يجب أن يذكر سبب دخوله هكذا — شراء مباشر، أو حالة طارئة، أو توريد مجاني من مقاول.",
  "Received without a purchase order{supplier}: {reason}": "استُلم بلا أمر شراء{supplier}: {reason}",
  " from {name}": " من {name}",
  "no delivery note": "بلا إشعار تسليم",
  "{n} booked in without an order. It is recorded as such.": "قُيد استلام {n} بلا أمر شراء. وسُجِّل ذلك.",

  /* an order raised elsewhere */
  "An order procurement raised in their own system. Recorded here so the desk can show what is coming and the delivery can be booked in against it — marked with the system it came from, so nobody mistakes it for one this register approved.":
    "أمر شراء أصدرته المشتريات في نظامها. يُسجَّل هنا لتُظهر الشاشة ما هو في الطريق ويمكن قيد استلامه عليه — مع ذكر النظام الذي صدر منه، حتى لا يظنه أحد أمراً اعتمده هذا السجل.",
  "Order number, as that system carries it": "رقم أمر الشراء كما يحمله ذلك النظام",
  "Raised in which system": "صادر من أي نظام",
  "raised in {system}": "صادر من {system}",
  "One line here; add the rest by recording the order again against the same number once that is supported, or load them with the stock.":
    "صنف واحد هنا؛ وتُضاف البقية بتسجيل الأمر مرة أخرى على الرقم نفسه متى دُعم ذلك، أو بتحميلها مع المخزون.",
  "An order needs the number it carries in the system it was raised in.":
    "أمر الشراء يحتاج الرقم الذي يحمله في النظام الذي صدر منه.",
  "Say which system this order was raised in, so nobody goes looking for it here.":
    "اذكر النظام الذي صدر منه هذا الأمر، حتى لا يبحث عنه أحد هنا.",
  "An order needs at least one line with a quantity.": "أمر الشراء يحتاج صنفاً واحداً على الأقل بكمية.",
  "{number} is already on the register.": "{number} موجود في السجل بالفعل.",
  "{number} recorded as raised in {system}. Its delivery can now be booked in.":
    "سُجِّل {number} كأمر صادر من {system}. ويمكن الآن قيد استلام توريده.",

  /* ── multi-line orders and deliveries (2026-09-21) ─────────────────── */
  "Add another line": "إضافة صنف آخر",
  "Line {n}": "الصنف {n}",
  "Quantity, line {n}": "الكمية، الصنف {n}",
  "Unit cost, line {n}": "تكلفة الوحدة، الصنف {n}",
  "Record it ({n} line)|Record it ({n} lines)":
    "سجّله|سجّله (صنف واحد)|سجّله (صنفان)|سجّله ({n} أصناف)|سجّله ({n} صنفاً)|سجّله ({n} صنف)",
  How: "الطريقة",
  "Enter the lines here": "إدخال الأصناف هنا",
  "Read them from a spreadsheet": "قراءتها من جدول",
  "Or paste the delivery, with its heading row": "أو الصق التوريد مع صف عناوينه",
  "A delivery with no order behind it — a direct purchase, an emergency buy, free issue from a contractor, a transfer in from another airport. Every line is ADDED to what the shelf already holds.":
    "توريد لا أمر شراء خلفه — شراء مباشر، أو شراء طارئ، أو توريد مجاني من مقاول، أو نقل من مطار آخر. وكل صنف يُضاف إلى ما يحمله الرف أصلاً.",
  "Each line is added to what that store already holds, and the catalog shows the new figure straight away.":
    "يُضاف كل صنف إلى ما يحمله ذلك المخزن أصلاً، ويعرض الكتالوج الرقم الجديد فوراً.",
  Delivered: "المورَّد",
  Into: "إلى",
  "On that shelf": "على ذلك الرف",
  After: "بعد",
  "{n} lines to book in, {units} units in all": "{n} أصناف للقيد، بمجموع {units} وحدة",
  "{n} that cannot be booked in and will be left out": "{n} تعذّر قيدها وستُستثنى",
  "Nothing in the delivery to book in.": "لا شيء في التوريد لقيده.",
  "There is nothing in the delivery to book in.": "لا يوجد في التوريد ما يُقيَّد.",
  "No line of this delivery can be booked in. Nothing was changed.":
    "لا يمكن قيد أي صنف من هذا التوريد. لم يتغير شيء.",
  "The delivery needs a heading row and at least one line beneath it.":
    "التوريد يحتاج صف عناوين وصنفاً واحداً على الأقل تحته.",
  "No catalog number column. Every line of a delivery needs one.":
    "لا يوجد عمود لرقم الكتالوج. كل صنف في التوريد يحتاج واحداً.",
  "No quantity column. A delivery with no quantities is a packing list, not a receipt.":
    "لا يوجد عمود للكمية. التوريد بلا كميات قائمة تعبئة لا قيد استلام.",
  "Read the spreadsheet first, so you can see what it would do.":
    "اقرأ الجدول أولاً لترى ما الذي سيحدث.",
  "a delivered quantity must be more than nothing": "الكمية المورَّدة يجب أن تكون أكثر من لا شيء",
  "not in the catalog — add the line first, or load it with the stock":
    "ليس في الكتالوج — أضف الصنف أولاً، أو حمّله مع المخزون",
  "not a line on {number}": "ليس صنفاً ضمن {number}",
  "{number} has {owed} outstanding on this line": "{number} به {owed} لم تُسلَّم على هذا الصنف",
  "{n} booked in, {units} units on to the shelf.": "قُيد {n}، و{units} وحدة إلى الرف.",
  "{n} line was left out.|{n} lines were left out.":
    "لم يُستثنَ شيء.|استُثني صنف واحد.|استُثني صنفان.|استُثنيت {n} أصناف.|استُثني {n} صنفاً.|استُثني {n} صنف.",

  /* the replenishment run, and not raising it twice */
  "Raise the replenishment ({n} line)|Raise the replenishment ({n} lines)":
    "رفع طلبات التجديد|رفع طلبات التجديد (صنف واحد)|رفع طلبات التجديد (صنفان)|رفع طلبات التجديد ({n} أصناف)|رفع طلبات التجديد ({n} صنفاً)|رفع طلبات التجديد ({n} صنف)",
  "Everything short is already asked for": "كل ناقص مطلوب بالفعل",
  "asked for on {n}": "مطلوب على {n}",
  "The one line below its reorder point has already been asked for. Nothing was raised twice.|Every line below its reorder point has already been asked for. Nothing was raised twice.":
    "لا شيء دون حد إعادة الطلب.|الصنف الوحيد دون حد إعادة الطلب مطلوب بالفعل. ولم يُرفع شيء مرتين.|الصنفان دون حد إعادة الطلب مطلوبان بالفعل. ولم يُرفع شيء مرتين.|كل الأصناف دون حد إعادة الطلب مطلوبة بالفعل. ولم يُرفع شيء مرتين.|كل الأصناف دون حد إعادة الطلب مطلوبة بالفعل. ولم يُرفع شيء مرتين.|كل الأصناف دون حد إعادة الطلب مطلوبة بالفعل. ولم يُرفع شيء مرتين.",
  "{n} line was already asked for and was not raised again.|{n} lines were already asked for and were not raised again.":
    "لم يُطلب شيء من قبل.|صنف واحد كان مطلوباً من قبل ولم يُرفع ثانية.|صنفان كانا مطلوبين من قبل ولم يُرفعا ثانية.|{n} أصناف كانت مطلوبة من قبل ولم تُرفع ثانية.|{n} صنفاً كانت مطلوبة من قبل ولم تُرفع ثانية.|{n} صنف كان مطلوباً من قبل ولم يُرفع ثانية.",

  /* the two categories the airport named for its own store */
  "AGL and airfield lighting": "إنارة المهبط (AGL)",
  Mechanical: "ميكانيكا",

  /* ── the whole inventory, and the store's own categories (2026-09-21) ── */
  "The whole inventory": "المخزون كاملاً",
  "Back to the desk": "العودة إلى الشاشة",
  "Every line the store holds, in every store and every bin, with nothing filtered out. Lines with no cost, lines nobody has ever drawn and lines taken off the working catalog are all here and marked, because a stock list that quietly omits its awkward rows is how a store comes to believe its own summary.":
    "كل صنف يحتفظ به المخزن، في كل مخزن وكل صندوق، بلا أي تصفية. الأصناف بلا تكلفة، والأصناف التي لم يُصرف منها شيء قط، والأصناف التي رُفعت من الكتالوج العامل، كلها هنا ومُعلَّمة، لأن قائمة مخزون تُسقط صفوفها المزعجة بصمت هي الطريق إلى أن يصدّق المخزن ملخّصه هو.",
  Find: "بحث",
  "a number, a name, a maker, a supplier": "رقم أو اسم أو صانع أو مورّد",
  "Grouped by": "مجمّعة حسب",
  "Not grouped": "بلا تجميع",
  "Every line": "كل الأصناف",
  "Nothing matches that.": "لا شيء يطابق ذلك.",
  "Used in the year": "المستهلك خلال العام",
  "no bin recorded": "لا صندوق مسجل",
  "nothing in a year": "لا حركة منذ عام",
  "Held by": "بعهدة",
  "nobody recorded": "لم يُسجَّل أحد",
  Lines: "الأصناف",
  "units on the shelves": "وحدة على الرفوف",
  categories: "فئات",
  "{n} carry no cost": "{n} بلا تكلفة",
  "{n} of {total} priced": "{n} من {total} مسعّرة",
  "Nowhere recorded": "لم يُسجَّل مكانه",
  "line|lines": "لا أصناف|صنف|صنفان|أصناف|صنفاً|صنف",
  "store|stores": "لا مخازن|مخزن|مخزنان|مخازن|مخزناً|مخزن",
  "Taken off the working catalogue": "مرفوعة من الكتالوج العامل",
  "kept with their movements; a catalogue number is never reused":
    "محفوظة مع حركاتها؛ ورقم الكتالوج لا يُعاد استخدامه أبداً",
  "By store": "حسب المخزن",
  "what each store holds, and what it is worth": "ما يحتفظ به كل مخزن، وكم يساوي",

  /* categories the storekeeper keeps */
  Categories: "الفئات",
  "what this store files under — the ones the platform ships with, and the ones you add":
    "ما يُصنَّف تحته هذا المخزن — ما تأتي به المنصة، وما تضيفه أنت",
  "What goes in it": "ما الذي يُوضع فيها",
  "A category this store needs": "فئة يحتاجها هذا المخزن",
  "Add the category": "إضافة الفئة",
  "ships with the platform": "تأتي مع المنصة",
  "De-icing chemicals": "مواد إزالة الجليد",
  "fluids, drums, spray nozzles": "سوائل وبراميل وفوهات رش",
  "A category needs a name.": "الفئة تحتاج اسماً.",
  "Say what goes in it. A category nobody can describe is one only its author files against correctly.":
    "اذكر ما الذي يُوضع فيها. الفئة التي لا يستطيع أحد وصفها لا يُصنِّف تحتها بشكل صحيح إلا من أنشأها.",
  "That name gives no key the register can use. Use letters or numbers in it.":
    "هذا الاسم لا يعطي مفتاحاً يمكن للسجل استخدامه. استخدم حروفاً أو أرقاماً فيه.",
  "{label} is already a category.": "{label} فئة موجودة بالفعل.",
  "{label} added. Lines can be filed under it now.": "أُضيفت {label}. ويمكن الآن تصنيف الأصناف تحتها.",
  "That category is one the platform ships with and the executive board names. It cannot be removed.":
    "هذه الفئة تأتي مع المنصة وتسميها لوحة الإدارة التنفيذية. ولا يمكن حذفها.",
  "{n} line is filed under it. Move it to another category first.|{n} lines are filed under it. Move them to another category first.":
    "لا أصناف تحتها.|صنف واحد مصنَّف تحتها. انقله إلى فئة أخرى أولاً.|صنفان مصنَّفان تحتها. انقلهما إلى فئة أخرى أولاً.|{n} أصناف مصنَّفة تحتها. انقلها إلى فئة أخرى أولاً.|{n} صنفاً مصنَّفة تحتها. انقلها إلى فئة أخرى أولاً.|{n} صنف مصنَّف تحتها. انقله إلى فئة أخرى أولاً.",
  "{n} category has been added to the ones the platform ships with. A category with lines filed under it cannot be removed until they are moved.|{n} categories have been added to the ones the platform ships with. A category with lines filed under it cannot be removed until they are moved.":
    "لم تُضف أي فئة إلى ما تأتي به المنصة.|أُضيفت فئة واحدة إلى ما تأتي به المنصة. والفئة التي تحتها أصناف لا تُحذف حتى تُنقل.|أُضيفت فئتان إلى ما تأتي به المنصة. والفئة التي تحتها أصناف لا تُحذف حتى تُنقل.|أُضيفت {n} فئات إلى ما تأتي به المنصة. والفئة التي تحتها أصناف لا تُحذف حتى تُنقل.|أُضيفت {n} فئة إلى ما تأتي به المنصة. والفئة التي تحتها أصناف لا تُحذف حتى تُنقل.|أُضيفت {n} فئة إلى ما تأتي به المنصة. والفئة التي تحتها أصناف لا تُحذف حتى تُنقل.",

  /* a part that is not on the list */
  "— a part that is not on the list —": "— صنف ليس في القائمة —",
  "Catalog number, line {n}": "رقم الكتالوج، الصنف {n}",
  "Name, line {n}": "الاسم، الصنف {n}",
  "Category, line {n}": "الفئة، الصنف {n}",
  "not in the catalog, and the row gives no name to create it with":
    "ليس في الكتالوج، والصف لا يعطي اسماً لإنشائه به",
  "{n} of them new to the catalog": "{n} منها جديدة على الكتالوج",
  "{n} part was new to the catalog and was added.|{n} parts were new to the catalog and were added.":
    "لا قطع جديدة.|قطعة واحدة كانت جديدة على الكتالوج وأُضيفت.|قطعتان كانتا جديدتين على الكتالوج وأُضيفتا.|{n} قطع كانت جديدة على الكتالوج وأُضيفت.|{n} قطعة كانت جديدة على الكتالوج وأُضيفت.|{n} قطعة كانت جديدة على الكتالوج وأُضيفت.",

  /* ── the counter (2026-09-21) ──────────────────────────────────────────
   *
   * On the terms: الصرف is issuing a part to a job; والمناولة عبر الكاونتر is
   * the act at the counter. The scan box's own words are kept short because
   * the person reading them has both hands full.
   */
  "The counter": "كاونتر الصرف",
  "Scan what is being taken and the job it is for, in any order, into the one box. A handheld scanner types what it reads and presses enter, so nothing needs to be installed or paired — and the same box works for somebody typing the number by hand.":
    "امسح ما يُؤخذ والعمل المخصص له، بأي ترتيب، في الخانة الواحدة. القارئ اليدوي يكتب ما يقرأه ويضغط إدخال، فلا شيء يحتاج تثبيتاً أو اقتراناً — والخانة نفسها تعمل لمن يكتب الرقم بيده.",
  "Scan or type": "امسح أو اكتب",
  "a catalogue number, a bin label, a manufacturer's part number, or the work order":
    "رقم كتالوج، أو ملصق صندوق، أو رقم قطعة من الصانع، أو أمر العمل",
  "scan here": "امسح هنا",
  "Taking parts out": "صرف قطع",
  "Putting parts back": "إرجاع قطع",
  "Taking parts out, or putting them back": "صرف القطع أو إرجاعها",
  "Job {n}": "العمل {n}",
  "This job has {n} reserved.": "لهذا العمل {n} محجوزة.",
  "add {qty} × {sku}": "أضف {qty} × {sku}",
  "{name} — found by {by}": "{name} — وُجد بواسطة {by}",
  "{name} — a store, not a part. Scan the part itself.": "{name} — مخزن لا قطعة. امسح القطعة نفسها.",
  "Nothing here is called {code}. Check the label, or add the part to the catalog first.":
    "لا شيء هنا يُسمى {code}. تحقق من الملصق، أو أضف القطعة إلى الكتالوج أولاً.",
  sku: "رقم الكتالوج",
  barcode: "الباركود",
  "part number": "رقم القطعة",
  reservation: "الحجز",

  "The basket": "السلة",
  "what is going across the counter": "ما يعبر الكاونتر",
  "what is coming back": "ما يعود",
  "Nothing scanned yet.": "لم يُمسح شيء بعد.",
  "Off which shelf": "من أي رف",
  "Quantity, {sku}": "الكمية، {sku}",
  "Hand over {n} unit|Hand over {n} units":
    "لا شيء لتسليمه|سلّم وحدة واحدة|سلّم وحدتين|سلّم {n} وحدات|سلّم {n} وحدة|سلّم {n} وحدة",
  "Put {n} unit back|Put {n} units back":
    "لا شيء لإرجاعه|أرجِع وحدة واحدة|أرجِع وحدتين|أرجِع {n} وحدات|أرجِع {n} وحدة|أرجِع {n} وحدة",
  "A line asking for more than that shelf holds will hand over what is there and say how many were short. Nothing is refused for being short.":
    "الصنف الذي يُطلب منه أكثر مما يحمله ذلك الرف يُسلَّم منه الموجود ويُذكر كم نقص. ولا يُرفض شيء لأنه ناقص.",

  "What went across": "ما عبر الكاونتر",
  "kept until the next scan, so the docket can be written up":
    "يبقى حتى المسح التالي، حتى يمكن تحرير السند",
  Given: "المُسلَّم",

  /* what the counter answers with */
  "Nothing has been scanned into the basket yet.": "لم يُمسح شيء إلى السلة بعد.",
  "Scan or type the work order first. Parts handed over against no job cannot be costed to one later.":
    "امسح أمر العمل أو اكتبه أولاً. القطع المسلَّمة بلا عمل لا يمكن تحميل تكلفتها على عمل لاحقاً.",
  "not in the catalog": "ليس في الكتالوج",
  "none on that shelf": "لا شيء على ذلك الرف",
  "{n} short — that is all this shelf held": "نقص {n} — هذا كل ما حمله هذا الرف",
  "{n} handed over against {wo}, {units} units.": "سُلِّم {n} على {wo}، و{units} وحدة.",
  "{n} put back, {units} units.": "أُرجع {n}، و{units} وحدة.",
  "{n} line was short.|{n} lines were short.":
    "لا نقص.|صنف واحد كان ناقصاً.|صنفان كانا ناقصين.|{n} أصناف كانت ناقصة.|{n} صنفاً كانت ناقصة.|{n} صنف كان ناقصاً.",

  /* the label on the bin */
  "The bin label": "ملصق الصندوق",
  "print it, stick it on the shelf, and the counter can scan it":
    "اطبعه والصقه على الرف، ويستطيع الكاونتر مسحه",
  "Barcode for {code}": "باركود {code}",
  "This number cannot be drawn as a Code 128 symbol.": "هذا الرقم لا يمكن رسمه كرمز Code 128.",
  "This bin already carries a barcode of its own — {code} — so the label uses it. The counter finds the part by either.":
    "هذا الصندوق يحمل باركوداً خاصاً به — {code} — فيستخدمه الملصق. والكاونتر يجد القطعة بأيٍّ منهما.",
  "The label carries the catalog number. Where a part already has a manufacturer's barcode on it, record that against the line instead and the counter will find it by either.":
    "يحمل الملصق رقم الكتالوج. وحيث تحمل القطعة باركود الصانع أصلاً، سجّله على الصنف بدلاً من ذلك، وسيجدها الكاونتر بأيٍّ منهما.",

  /* ── the logistics suite, with the register behind it (2026-09-21) ──── */
  "The stores behind these figures": "المخازن خلف هذه الأرقام",
  "every number on this board is read from the register — this is the register":
    "كل رقم في هذه اللوحة مقروء من السجل — وهذا هو السجل",
  "The stores desk": "شاشة المخازن",
  "deliveries overdue": "توريدات متأخرة",
  "These do the same thing the stores desk does, to the same register. The board's own buttons beneath are the demonstration's and write nowhere.":
    "هذه تفعل ما تفعله شاشة المخازن، على السجل نفسه. أما أزرار اللوحة أدناه فهي للعرض التوضيحي ولا تكتب في أي مكان.",
  "What is short, and where to go about it": "ما هو ناقص، وأين تعالجه",
  "the lines behind the figures above, each one a link into the register":
    "الأصناف خلف الأرقام أعلاه، كل منها رابط إلى السجل",
  "Nothing is out of stock or below its reorder point.": "لا شيء نفد أو نزل دون حد إعادة طلبه.",
  "Jobs waiting": "أعمال تنتظر",

  /* ── a job waiting for a part (2026-09-21) ─────────────────────────────
   *
   * On the terms: تعليق is the hold itself; مستوى الخدمة is the service level
   * the wait would otherwise eat. The distinction the whole feature rests on
   * — only a parts hold stops the clock — is carried in the sentences rather
   * than in a word, because Arabic has no shorter way to say it either.
   */
  "Waiting for parts: {names} — {when}.": "بانتظار قطع: {names} — {when}.",
  "nothing is on order": "لا طلب شراء قائم",
  "due {when}": "مستحقة {when}",
  "no delivery date": "لا تاريخ تسليم",
  "{n} part is short and nothing is on order. Time spent waiting will count against this job's service level unless it is held.|{n} parts are short and nothing is on order. Time spent waiting will count against this job's service level unless it is held.":
    "لا نقص.|تنقص قطعة واحدة ولا طلب شراء لها. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص قطعتان ولا طلب شراء لهما. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص {n} قطع ولا طلب شراء لها. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص {n} قطعة ولا طلب شراء لها. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص {n} قطعة ولا طلب شراء لها. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.",
  "{n} part is short. Time spent waiting will count against this job's service level unless it is held.|{n} parts are short. Time spent waiting will count against this job's service level unless it is held.":
    "لا نقص.|تنقص قطعة واحدة. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص قطعتان. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص {n} قطع. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص {n} قطعة. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.|تنقص {n} قطعة. وسيُحتسب وقت الانتظار على مستوى خدمة هذا العمل ما لم يُعلَّق.",
  "This job is short of a part, but a job that is {status} cannot be put on hold. Until it is, the wait counts against its service level.":
    "هذا العمل تنقصه قطعة، لكن العمل الذي حالته {status} لا يمكن تعليقه. وإلى أن يُعلَّق، يُحتسب الانتظار على مستوى خدمته.",
  "Every part this job was waiting for is on the shelf. Nothing is holding it now.":
    "كل قطعة كان ينتظرها هذا العمل صارت على الرف. ولا شيء يعيقه الآن.",

  /* the timeline */
  "How the parts got here": "كيف وصلت القطع",
  "Parts reserved for this job": "حُجزت قطع لهذا العمل",
  "The shelf could not fill it": "لم يستطع الرف تلبيته",
  "{sku}: {n} short": "{sku}: نقص {n}",
  "Requisition {number} raised": "رُفع طلب الشراء {number}",
  "waiting on a decision": "بانتظار قرار",
  "approved by {who}": "اعتمده {who}",
  "became {number}": "صار {number}",
  "Ordered on {number} from {supplier}": "طُلب على {number} من {supplier}",
  "Delivery expected": "التسليم متوقع",
  "The delivery is late": "التسليم متأخر",
  "{n} outstanding on {number}": "{n} لم تُسلَّم على {number}",
  "Delivered and booked in": "سُلِّم وقُيد",
  "Issued to the job": "صُرف للعمل",
  "Every part is with the job": "كل القطع مع العمل",

  /* the card itself */
  "what is reserved against this job, where it has got to, and what the wait is costing":
    "ما هو محجوز على هذا العمل، وأين وصل، وكم يكلّف الانتظار",
  "Put the job on hold for parts": "تعليق العمل بانتظار القطع",
  "Take it off hold — the parts are in": "رفع التعليق — وصلت القطع",
  "Parts are in. Work restarts.": "وصلت القطع. يُستأنف العمل.",
  "This job was already past its window when the hold was recorded. A hold stops further time counting against it; it does not undo what had already passed.":
    "كان هذا العمل قد تجاوز نافذته قبل تسجيل التعليق. والتعليق يوقف احتساب ما بعده، ولا يلغي ما مضى.",
  "This job is held for parts, so the wait is off its service level from now on{sofar}. It reads {state} — {remaining}.":
    "هذا العمل معلَّق بانتظار قطع، فالانتظار خارج مستوى خدمته من الآن{sofar}. وهو يقرأ {state} — {remaining}.",
  " — {days} so far": " — {days} حتى الآن",
  "{days} of this job's life were spent waiting for a part. That time is off its service level, which now reads {state} — {remaining}.":
    "قضى هذا العمل {days} بانتظار قطعة. وذلك الوقت خارج مستوى خدمته، الذي يقرأ الآن {state} — {remaining}.",
  "No time has been taken off this job's service level. Only a hold entered for parts does that, and a hold for anything else keeps the clock running.":
    "لم يُخصم أي وقت من مستوى خدمة هذا العمل. فقط التعليق المسجَّل بسبب القطع يفعل ذلك، وأي تعليق آخر يُبقي الساعة تعمل.",

  /* the message to whoever is doing the work */
  "{number}: the parts you were waiting for are in.": "{number}: وصلت القطع التي كنت تنتظرها.",
  "Held for you at the stores:": "محجوزة لك في المخازن:",
  "Everything for this job has already been issued.": "كل ما يخص هذا العمل صُرف بالفعل.",
  "Collect them at the counter against {number}. The job comes off hold when work restarts.":
    "استلمها من الكاونتر على {number}. ويُرفع تعليق العمل عند استئنافه.",
  "whoever is doing the work": "من يقوم بالعمل",
  "The parts are on the shelf. {who} has not been told yet.": "القطع على الرف. ولم يُبلَّغ {who} بعد.",
  "Copy the message": "نسخ الرسالة",
  Copied: "نُسخت",
  "The platform sends nothing: it has no outbound channel. Copy this into whatever the airport already uses, and the job's own record shows the parts were ready from the moment they were booked in.":
    "المنصة لا ترسل شيئاً: ليس لديها قناة إرسال. انسخ هذا إلى ما يستخدمه المطار أصلاً، وسجل العمل نفسه يُظهر أن القطع كانت جاهزة منذ لحظة قيدها.",
};

export default ar;
